import classNames from "classnames";
import dayjs from "dayjs";

import { AscBadgeColor, handleStopPropagation } from "@ascendnow/shared";
import AscBadge from "../../AscBadge/AscBadge";
import AscButton from "../../AscButton/AscButton";
import AscCircularProgressBar from "../../AscCircularProgressBar/AscCircularProgressBar";
import { CalendarEmptyIcon, CheckActiveIcon, LockIcon } from "../../AscIcons";
import AscLink from "../../AscLink/AscLink";
import AscTag from "../../AscTag/AscTag";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscChallengeCollectionCard.module.scss";

export interface AscChallengeCollectionCardProps {
  title: string;
  coverImageUrl?: string;
  locked?: boolean;
  date?: string;
  mainChallengePath?: string;
  challengeCollectionPath?: string;
  /**
   * The total number of challenges in this collection excluding the main challenge
   */
  totalChallenges: number;
  /**
   * Number of completed challenges in this collection excluding the main challenge
   */
  completedChallenges: number;
  mainChallengeCompleted: boolean;
  skillColor: AscBadgeColor;
  className?: string;
  lockedMessage?: string;
  unlockedMessage?: string;
  loading?: boolean;
  disabled?: boolean;
}

export default function AscChallengeCollectionCard({
  title,
  coverImageUrl,
  locked,
  date,
  totalChallenges,
  completedChallenges,
  skillColor,
  className,
  mainChallengePath,
  challengeCollectionPath,
  lockedMessage = "Complete all the challenges for this skill and unlock the final skill challenge",
  unlockedMessage = "You’ve completed all the challenges, and now you can proceed to the main challenge!",
  mainChallengeCompleted,
  loading = false,
  disabled = false,
}: AscChallengeCollectionCardProps) {
  return (
    <AscGenericCard
      tagsContainerClassName={styles.tagsContainer}
      tags={[
        <AscBadge noGaussianBlur color={skillColor} className={styles.badge} />,
        <div className={styles.tag}>
          <AscCircularProgressBar
            className={styles.circularProgressBar}
            color={"var(--pink)"}
            hideNumber
            percentage={(completedChallenges / totalChallenges) * 100}
          />
          <span className={styles.tagLabel}>Challenges:</span>
          <span className={styles.tagValue}>
            {completedChallenges}/{totalChallenges}
          </span>
        </div>,
        <CheckActiveIcon className={styles.checkIcon} />,
      ]}
      title={title}
      coverImageUrl={coverImageUrl}
      imageClassName={styles.image}
      data-testid="asc-challenge-collection-card"
      className={classNames(
        styles.ground,
        {
          [styles.locked]: locked,
          [styles.completed]: mainChallengeCompleted,
          [styles.loading]: loading,
        },
        className
      )}
      footerClassName={styles.footer}
      loading={loading}
      footer={
        <>
          <AscLink
            className={styles.listOfChallengesButton}
            href={challengeCollectionPath}
            onClick={handleStopPropagation()}
            disabled={disabled}
          >
            <AscButton variant="secondary" size="small">
              List of challenges
            </AscButton>
          </AscLink>
          <AscLink
            className={styles.exploreButton}
            href={mainChallengePath}
            onClick={handleStopPropagation()}
            disabled={locked || disabled}
          >
            <AscButton size="small" disabled={locked}>
              Explore
            </AscButton>
          </AscLink>
          <div className={styles.date}>
            <CalendarEmptyIcon className={styles.calendarIcon} />
            <span className={styles.time}>
              {dayjs(date).format("D MMM[,] YYYY")}
            </span>
          </div>
          <AscTag
            className={styles.completedTag}
            labelClassName={styles.completedTagLabel}
            label="Completed"
          />
        </>
      }
      contentClassName={styles.content}
    >
      <LockIcon className={styles.lockIcon} />
      <AscParagraph className={styles.lockedMessage}>
        {locked ? lockedMessage : unlockedMessage}
      </AscParagraph>
    </AscGenericCard>
  );
}
